import type {PropsWithChildren} from 'react';
import React, {createContext, useContext, useMemo} from 'react';
import type {IAccessClaim} from '@igp/shared/access-control';
import {useAuthAccessClaims} from '@igp/shared/access-control';
import {getNonEmptyValueValidator} from '@igp/shared/redux/view';

import {useAgentId} from '@features/account/hooks';
import {useDebtResponsibleMasterAgents} from '@features/debt-responsible-master-agent';

import {BoAgentAccessClaimParser} from './accessClaimParser';
import {DebtResponsibleMasterAgentAccessClaim} from './DebtResponsibleMasterAgentAccessRule';

type AccessControlContextProps = {
    claims: IAccessClaim[];
};

const AccessControlContext = createContext<AccessControlContextProps>(null);

export function AccessControlProvider({children}: PropsWithChildren) {
    const agentUid = useAgentId();
    const item = useDebtResponsibleMasterAgents({
        viewType: 'AccessControlProvider',
        fields: ['agent_uid'],
        defaultFilters: [
            {
                key: 'agent_uid',
                value: agentUid,
            },
        ],
        validateFilter: getNonEmptyValueValidator('agent_uid'),
        isCancelable: false,
    })?.items?.[0];
    const parser = useMemo(() => new BoAgentAccessClaimParser(), []);
    const claims = [...useAuthAccessClaims(parser), new DebtResponsibleMasterAgentAccessClaim(item)];
    return (
        <AccessControlContext.Provider
            value={{
                claims,
            }}
        >
            {children}
        </AccessControlContext.Provider>
    );
}

export function useAccessControlContext() {
    return useContext(AccessControlContext);
}
